import React, { useState } from 'react';
import { Button } from './ui';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  alt?: string;
  title?: string;
  mimeType?: string;
}

export function ImageModal({ isOpen, onClose, src, alt, title, mimeType }: ImageModalProps) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  const isPDF = mimeType === 'application/pdf';
  const isImage = !isPDF && (mimeType?.startsWith('image/') || !mimeType);

  if (!isOpen) return null;

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setScale(1);
    setRotation(0);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="relative w-full h-full p-4">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center mb-4 text-white">
          <div>
            <h3 className="text-lg font-medium">{title || alt}</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Contenu de prévisualisation */}
        <div className="relative overflow-auto h-full flex items-center justify-center">
          {isPDF ? (
            <embed
              src={src}
              type="application/pdf"
              className="w-full h-full"
            />
          ) : (
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{
                transform: `scale(${scale}) rotate(${rotation}deg)`,
                cursor: scale > 1 ? 'grab' : 'default',
              }}
              draggable={false}
            />
          )}
        </div>

        {/* Contrôles (uniquement pour les images) */}
        {isImage && (
          <div className="absolute bottom-0 left-0 right-0 flex justify-center space-x-2 p-4 bg-black/50">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomOut}
              disabled={scale <= 0.25}
              className="bg-white/80 hover:bg-white"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleReset}
              className="bg-white/80 hover:bg-white"
            >
              {Math.round(scale * 100)}%
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomIn}
              disabled={scale >= 3}
              className="bg-white/80 hover:bg-white"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM12 10v3m0 0v3m0-3h3m-3 0H9" />
              </svg>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleRotate}
              className="bg-white/80 hover:bg-white"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}